// @systeme: interface
// « Un souci, une idée ? » : l'utilisatrice nous dit ce qui ne va pas, en deux gestes.
// Disponible partout, jamais imposé. C'est la porte d'entrée de l'amélioration continue.
import { useState } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import type { CibleRetour, TypeRetour } from '@foyer/shared';
import { api } from '../lib/api';
import { textes } from '../lib/textes';
import { useMessages } from './Messages';

const TYPES: { valeur: TypeRetour; libelle: string }[] = [
  { valeur: 'insatisfaction', libelle: 'Ça ne va pas' },
  { valeur: 'suggestion', libelle: 'Une idée' },
  { valeur: 'bug', libelle: 'Un bug' },
  { valeur: 'contenu_incorrect', libelle: 'C’est faux' },
];

export interface CibleDuRetour {
  cible: CibleRetour;
  cibleId?: string;
}

interface Props {
  ouvert: boolean;
  onFermer(): void;
  ecran: string;
  cible?: CibleDuRetour;
}

export function FormulaireRetour({ ouvert, onFermer, ecran, cible }: Props) {
  const { dire, direErreur } = useMessages();
  const [type, setType] = useState<TypeRetour>('insatisfaction');
  const [message, setMessage] = useState('');
  const [envoi, setEnvoi] = useState(false);

  async function envoyer() {
    if (message.trim().length === 0) return;
    setEnvoi(true);
    try {
      await api.retours.envoyer({
        type,
        cible: cible?.cible ?? 'aucune',
        cibleId: cible?.cibleId ?? null,
        ecran,
        message,
      });
      dire(textes.merciRetour);
      setMessage('');
      onFermer();
    } catch (e) {
      direErreur(e);
    } finally {
      setEnvoi(false);
    }
  }

  return (
    <Dialog open={ouvert} onClose={onFermer} fullWidth maxWidth="xs">
      <DialogTitle>Un souci, une idée ?</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <ToggleButtonGroup
            exclusive
            value={type}
            onChange={(_e, v: TypeRetour | null) => v && setType(v)}
            fullWidth
            size="small"
            sx={{ flexWrap: 'wrap' }}
          >
            {TYPES.map((t) => (
              <ToggleButton key={t.valeur} value={t.valeur} sx={{ flex: '1 1 45%' }}>
                {t.libelle}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <TextField
            label="Dis-nous en un mot"
            multiline
            minRows={3}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            autoFocus
            inputProps={{ maxLength: 2000 }}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onFermer} color="inherit">
          Plus tard
        </Button>
        <Button
          onClick={envoyer}
          variant="contained"
          disabled={envoi || message.trim().length === 0}
        >
          Envoyer
        </Button>
      </DialogActions>
    </Dialog>
  );
}
